import { validate } from 'uuid'
import { format, utcToZonedTime } from 'date-fns-tz'
import type { FormikErrors } from 'formik'
import { isValid } from 'date-fns'
import { formatValue } from 'react-currency-input-field'
import { isValidPhoneNumber } from 'react-phone-number-input'

import { toast } from '@helpers/toaster'

import i18n from '@src/i18n'
import { RequestErrorHandler } from './ErrorHandler'

export { getNestedError } from './ErrorHandler/utils'

export const checkIsLink = (link: string) => {
  const regex = /(https?|slack:\/\/([-\w\.]+)+(:\d+)?(\/([\w\/_\.]*(\?\S+)?)?)?)/gi

  return regex.test(link)
}

export const checkIsFile = (fileLink: string) => {
  if (fileLink.startsWith(window.location.origin)) {
    const uuid = fileLink?.split('/')?.pop()
    if (uuid) {
      return validate(uuid)
    }
  }

  return false
}

export const str2num = (str?: string) => {
  if (typeof str !== 'string' || !str) {
    return 0
  }
  let newStr = str
  if (str.includes(',') && !str.includes('.')) {
    newStr = str.replace(',', '.')
  }
  const [i, f] = newStr.split('.')
  const val = {
    integer: i ? i.replace(/\s/g, '') : 0,
    fraction: f || 0,
  }

  return +`${val.integer}.${val.fraction}`
}

export const getCurrencyInfo = (currency: string) => {
  const parts = Intl.NumberFormat('en-US', { style: 'currency', currency })?.formatToParts(1)
  const sign = parts?.find((x) => x.type === 'currency')?.value || ''
  const literal = parts?.find((x) => x.type === 'literal')?.value || ''
  const hasDecimal = !!parts?.find((x) => x.type === 'decimal')?.value
  return { sign, literal, hasDecimal }
}

/**
 * New self-describing amount shape: `{value: 1000, digits: 3}` means 1.000
 * (value is an integer scaled by 10^digits). Legacy amounts are plain
 * numbers/strings in cents (scale 2) and are never migrated.
 */
export type AmountObjectValue = { value: number; digits: number }

export const isAmountObject = (raw: unknown): raw is AmountObjectValue =>
  typeof raw === 'object' &&
  raw !== null &&
  'value' in raw &&
  'digits' in raw &&
  Number.isFinite((raw as AmountObjectValue).value) &&
  Number.isInteger((raw as AmountObjectValue).digits)

/** Human-scale number for either amount shape (for math, not display). */
export const amountToNumber = (
  raw: string | number | AmountObjectValue | null | undefined,
  isFixedPoint = true,
): number => {
  if (isAmountObject(raw)) {
    return raw.value / Math.pow(10, raw.digits)
  }
  const numberValue = +(raw ?? 0)
  return isFixedPoint ? numberValue / 100 : numberValue
}

/**
 * Decimal precision the FX conversion rate is stored at, for ALL teams.
 * Decoupled from the amount-format pilot (`amount_digits`): every new request
 * persists the rate as the self-describing `{value, digits: FX_RATE_DIGITS}`
 * shape so rates like `1.2345` aren't truncated by the legacy ×100 (2dp) scale.
 * Backend readers decode either shape, so legacy stored rates are unaffected.
 */
export const FX_RATE_DIGITS = 4

/**
 * FX conversion-rate multiplier from either storage shape. The legacy rate is
 * the multiplier scaled by 100 (`125` -> `1.25`); the `{value, digits}` shape
 * decodes to its own scale. A falsy/empty rate means no conversion and
 * resolves to `fallback` (1 for the team base currency; some call sites
 * historically defaulted to 0).
 */
export const readRate = (raw: number | string | AmountObjectValue | null | undefined, fallback = 1): number => {
  // Decode either shape to the multiplier, then apply the same fallback on both
  // paths so 0 / '0' / NaN / empty all resolve consistently.
  const value = isAmountObject(raw) ? amountToNumber(raw) : +(raw ?? 0) / 100
  return value || fallback
}

/**
 * Stored FX rate as a form-input string, preserving the stored shape's
 * precision: the `{value, digits}` shape renders with its own digits (so a
 * 3-digit rate survives an edit-reopen), the legacy rate renders at
 * `fallbackDigits`. Falsy/empty resolves to `fallback`.
 */
export const rateToInput = (
  raw: number | string | AmountObjectValue | null | undefined,
  fallback = 1,
  fallbackDigits = 2,
): string => {
  if (isAmountObject(raw)) return amountToNumber(raw).toFixed(raw.digits)
  return readRate(raw, fallback).toFixed(fallbackDigits)
}

/**
 * Format number value to currency string
 * @param {number|string|AmountObjectValue} value value to format
 * @param {{currency?:string, decimalScale?:number, prefix?:string, suffix?:string}} [options]
 * @param {string} [options.currency] currency to use
 * @param {number} [options.decimalScale=2] decimal scale to round to (ignored for the {value, digits} shape — its own digits win)
 * @param {string} [options.prefix] prefix to add to the value
 * @param {string} [options.suffix] suffix to add to the value
 * @param {boolean} [options.isFixedPoint=true] whether the value is fixed point
 * @param {boolean} [options.isCurrency=true] whether the value is currency
 * @returns {string} formatted string
 */
export const val2Amount = (
  value: string | number | AmountObjectValue,
  options?: {
    currency?: string
    decimalScale?: number
    prefix?: string
    suffix?: string
    isFixedPoint?: boolean
    isCurrency?: boolean
  },
): string => {
  const { isCurrency = true } = options || {}
  const { hasDecimal } = options?.currency ? getCurrencyInfo(options.currency) : { hasDecimal: true }

  let numberValue: number
  let objectDigits: number | undefined

  if (isAmountObject(value)) {
    objectDigits = value.digits
    numberValue = value.value / Math.pow(10, value.digits)
  } else {
    numberValue = +value
    if (options?.isFixedPoint ?? true) {
      numberValue = numberValue / 100
    }
  }

  return formatValue({
    value: numberValue.toString(),
    suffix: options?.suffix,
    prefix: options?.prefix,
    intlConfig: {
      locale: 'en-US',
      currency: isCurrency ? options?.currency : undefined,
    },
    decimalScale: objectDigits ?? (hasDecimal ? options?.decimalScale ?? 2 : 0),
  })
}

export const sanitizeInput = (string: string): string => {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  }
  const reg = /[&<>"'/]/gi
  return string.replace(reg, (match: string) => map[match as keyof typeof map])
}

export const getCeilValue = (value: number) => {
  return Math.round(value * 100) / 100
}

export const formatDate = (date?: Date | string | null, withTime = true, withTimezone = false): string => {
  try {
    if (!date) {
      return ''
    }
    if (!withTime) {
      return format(typeof date === 'string' ? new Date(date) : date, 'yyyy-MM-dd')
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const rawDate = utcToZonedTime(date, timezone, {
      timeZone: 'UTC',
    })

    return format(rawDate, `yyyy-MM-dd hh:mm a` + (withTimezone ? ` zzz` : ''))
  } catch {
    return ''
  }
}

export const formatTime = (dateTime?: string | null, timezone?: string): string => {
  try {
    if (!dateTime) return ''

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const isTimeOnly = !isValid(new Date(dateTime))
    const formattedDate = isTimeOnly
      ? utcToZonedTime(`${format(new Date(), 'yyyy-MM-dd')}T${dateTime}`, userTimezone, { timeZone: timezone || 'UTC' })
      : utcToZonedTime(dateTime, userTimezone, { timeZone: timezone || 'UTC' })

    return format(formattedDate, 'hh:mm a')
  } catch {
    return ''
  }
}

/**
 * Legacy function for backward compatibility
 * @param err - Error object
 * @param errorText - Optional error text
 * @returns Formatted error message
 */
export const requestErrorsHandler = (err: unknown, errorText?: string): string | null => {
  return new RequestErrorHandler(err, errorText).handle()
}

export const parseJWT = <T = Record<string, unknown>>(token: string): T => {
  try {
    return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString())
  } catch (e) {
    return {} as T
  }
}

export const getInitials = (name?: string | undefined) => {
  if (!name) return 'AP'
  const ignoredWords = ['the', 'a', 'and', '&', 'of']
  const words = name.split(' ').filter((word: string) => {
    return !ignoredWords.includes(word.toLowerCase())
  })

  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase()
  } else if (words.length >= 2) {
    return (words?.at(0)?.at(0) || '' + words?.at(1)?.at(0) || '').toUpperCase()
  }
  return 'AP'
}

export const copyToClipboard = async (text: string) => {
  if (!text || typeof text !== 'string') return
  try {
    await navigator.clipboard.writeText(text)
    toast.success(i18n.t('settings:verification.typeCode.clipboardCopySuccess') as string)
  } catch (err) {
    toast.error(i18n.t('settings:verification.errors.generalError') as string)
  }
}

export const getFirstError = (obj: FormikErrors<unknown>): null | string => {
  const prop = Object.values(obj)?.[0]
  if (!prop) {
    return null
  }
  return typeof prop === 'string' ? prop : getFirstError(prop)
}

// Validates E.164 numbers (the format `react-phone-number-input` emits)
// using libphonenumber-js's per-country numbering plans. Returns `true` for
// empty/undefined so optional Yup `.test()` validators skip; required fields
// should pair this with `.required()`.
export const isValidPhone = (value: string | undefined | null): boolean => {
  if (!value) return true
  return isValidPhoneNumber(value)
}
